import styles from "../../../../components/SelectTrigger/internal/SelectTriggerBase.module.scss";
import cx from "classnames";
import { PassThroughProps, Size } from "../../../../types";
import { Button } from "../../../../components/Button";
import Chevron_Right from "@servicetitan/hammer-icon/mdi/round/chevron_right.svg";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import { ReactElement, RefObject } from "react";

/**
 * Props for the SelectFieldInput component
 * @property {string} [placeholder] - Placeholder text displayed when input is empty
 * @property {Extract<Size, "small" | "medium" | "large">} [size] - Size variant of the input
 * @property {boolean} [disableClearButton] - Hides the clear button when true
 * @property {boolean} [disableToggleButton] - Hides the toggle button when true
 * @property {boolean} [disabled] - Disables the input entirely
 * @property {boolean} [readOnly] - Cannot be modified but remains interactive
 * @property {boolean} [required] - Marks the input as required for form validation
 * @property {boolean} [error] - Shows error styling on the input when true
 * @property {() => void} [onClear] - Callback fired when the clear button is clicked
 * @property {string} [id] - HTML id attribute for the input element
 * @property {RefObject<HTMLDivElement>} [inputWrapperRef] - Ref for the input wrapper div element
 * @property {PassThroughProps<"div">} [htmlInputWrapperProps] - Additional props passed to the wrapper div
 * @property {PassThroughProps<"input">} [inputProps] - Additional props passed to the input element
 * @property {PassThroughProps<"button">} [toggleButtonProps] - Additional props passed to the toggle button
 * @property {string | ReactElement} [prefix] - Content to display before the input
 * @property {string | ReactElement} [suffix] - Content to display after the input
 */
export type SelectFieldInputProps = {
  placeholder?: string;
  size?: Extract<Size, "small" | "medium" | "large">;
  disableClearButton?: boolean;
  disableToggleButton?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  error?: boolean;
  onClear?: () => void;
  id?: string;
  inputWrapperRef?: RefObject<HTMLDivElement>;
  htmlInputWrapperProps?: PassThroughProps<"div">;
  inputProps?: PassThroughProps<"input">;
  toggleButtonProps?: PassThroughProps<"button">;
  prefix?: string | ReactElement;
  suffix?: string | ReactElement;
};

/**
 * Internal input component for the SelectField that provides the text input and action buttons.
 *
 * Features:
 * - Text input for filtering and displaying selected values
 * - Optional clear button for removing the current selection
 * - Optional toggle button for opening/closing the dropdown menu
 * - Supports multiple size variants (small, medium, large)
 * - Fully accessible with proper ARIA labels on buttons
 * - Supports pass-through props for customization of internal elements
 * - Optional prefix and suffix content
 * - Error state styling
 *
 * @example
 * <SelectFieldInput
 *   placeholder="Search..."
 *   size="medium"
 *   onClear={() => handleClear()}
 *   prefix="$"
 * />
 */
export const SelectFieldInput = ({
  placeholder,
  size,
  disableClearButton,
  disableToggleButton,
  disabled,
  readOnly,
  required,
  error,
  onClear,
  id,
  inputWrapperRef,
  htmlInputWrapperProps,
  inputProps,
  toggleButtonProps,
  prefix,
  suffix,
}: SelectFieldInputProps) => {
  const inputWrapperClassNames = cx(styles["input-wrapper"], {
    [styles["small"]]: size === "small",
    [styles["large"]]: size === "large",
    [styles["no-clear-button"]]: disableClearButton,
    [styles["no-toggle-button"]]: disableToggleButton,
    [styles["disabled"]]: disabled || readOnly,
  });

  return (
    <div
      ref={inputWrapperRef}
      {...htmlInputWrapperProps}
      className={cx(inputWrapperClassNames, htmlInputWrapperProps?.className)}
    >
      {prefix ? <div className={styles["prefix"]}>{prefix}</div> : null}
      <input
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        {...inputProps}
        className={cx(styles["input"], inputProps?.className, {
          [styles["error"]]: error,
        })}
      />
      {suffix ? <div className={styles["suffix"]}>{suffix}</div> : null}
      <div className={styles["buttons-wrapper"]}>
        {disableClearButton ? null : (
          <div className={styles["close-button-wrapper"]}>
            <Button
              icon={Close}
              aria-label="clear selection"
              appearance="ghost"
              size="small"
              className={styles["close-button"]}
              onClick={() => {
                onClear?.();
              }}
            />
          </div>
        )}
        {disableToggleButton ? null : (
          <div className={styles["toggle-button-wrapper"]}>
            <Button
              icon={Chevron_Right}
              aria-label="toggle menu"
              appearance="ghost"
              size="small"
              disabled={disabled}
              {...toggleButtonProps}
              className={cx(
                styles["toggle-button"],
                toggleButtonProps?.className,
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};

SelectFieldInput.displayName = "SelectFieldInput";
